import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { Link } from "react-router-dom";

const PainCard = ({ dot, cat, title, body, stake }) => (
  <div className="card">
    <div className="cat">
      <span className="d" style={{ background: dot }} />
      {cat}
    </div>
    <h3>{title}</h3>
    <p>{body}</p>
    <div className="stake">{stake}</div>
  </div>
);

const Home = () => (
  <>
    <Seo
      title="Clearview — ask your business anything. Get a straight answer."
      description="Clearview reads your real numbers — Xero, QuickBooks, WorkflowMax — and gives you a straight answer, any time you need one. Read-only, month to month, no lock-in."
      path="/"
    />
    <Header />
    <main id="top">
      {/* HERO */}
      <section className="hero" data-testid="home-hero">
        <div className="glow" />
        <div className="container">
          <Reveal>
            <div className="eyebrow eyebrow-light">A straight answer · No pitch</div>
            <h1 style={{ marginTop: 18 }}>
              Can we afford it?<br />Should we hire?<br />
              <span className="gtext">Just ask your business.</span>
            </h1>
            <p className="sub">
              Clearview reads your real numbers — Xero, QuickBooks, WorkflowMax — and gives you a straight answer, any time you need one. Daily, hourly, before any decision. Independent, plain English, and it takes a side.
            </p>
            <div className="cta">
              <Link className="pill" to="/apply" data-testid="hero-cta-primary">Book a free demo</Link>
              <a className="ghost" href="#proof" data-testid="hero-cta-watch">Or watch it decide <span className="arr">↓</span></a>
            </div>
            <div className="chips">
              <span>Plain English</span><span>Read-only</span><span>No lock-in</span><span>Australian-built</span>
            </div>
          </Reveal>

          <Reveal>
            <div className="pcard">
              <div className="top">
                <span><span className="dot" />Ask Clearview</span>
                <span className="refused">Refused 35 in 24h</span>
              </div>
              <div className="body">
                <div className="ask"><span>Can we afford a new van — it's $90k?</span></div>
                <div className="lab">Answer</div>
                <div className="ans">
                  <span className="no">Not right now.</span> You've $174k cash, but after ~$19k set aside for BAS/PAYG/super you're at $155k. A $90k van drops you to $65k — barely a month of cover at ~$60k/mo.
                </div>
                <div className="best">
                  <div className="l">⚡ Best move now</div>
                  <p>Chase the $14,218 overdue (MBC $6,144, Brendale $4,048, Novar $3,322) — 16% of the van sitting unpaid.</p>
                </div>
                <div className="opts">
                  <div className="opt"><div className="t">Safest</div><div className="h">Defer 90 days</div></div>
                  <div className="opt"><div className="t">Growth</div><div className="h">Finance it</div></div>
                  <div className="opt"><div className="t">Bold</div><div className="h">Buy outright</div></div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATEMENT */}
      <section className="band-white pad statement center">
        <div className="container">
          <Reveal>
            <h2>Be the one who<br /><span className="gtext">isn't guessing.</span></h2>
            <p className="lede">
              Every owner on your street is making the big calls on gut — the hire, the price rise, the van, the customer they should've chased. Some are guessing right. Most are finding out weeks too late.
            </p>
            <div className="rule" />
            <p className="kick">
              Nobody looks at your numbers for you. Your accountant looks for the tax office. Your bookkeeper looks for the reconciliation. Clearview looks for you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* DECISIONS */}
      <section className="band-grey pad" id="decisions">
        <div className="container">
          <Reveal className="center">
            <div className="eyebrow">The questions you carry alone</div>
            <h2 style={{ fontSize: "clamp(30px,4.6vw,46px)", color: "var(--navy)", marginTop: 14 }}>
              Ask it the ones that keep you up.
            </h2>
            <p className="sub-h">Not "show me a chart." The real ones — answered straight, on your actual numbers, the moment you need it.</p>
          </Reveal>
          <Reveal className="grid g3" style={{ marginTop: 44 }}>
            <PainCard dot="var(--t)" cat="Hiring" title={'"Can we afford another person?"'}
              body="It checks the cash, the cover, the pipeline — and tells you hire, wait, or fix the gap first."
              stake="Get it wrong and it's $80k+ you can't unwind" />
            <PainCard dot="var(--b)" cat="Cash" title={'"Will we make payroll and the BAS?"'}
              body="It sets aside what's owed, shows the real runway, and flags the dip before it lands."
              stake="The surprise that ruins a whole month" />
            <PainCard dot="var(--p)" cat="Customers" title={'"Is a customer about to go quiet?"'}
              body="It spots the cadence shift and the overdue concentration before it becomes a bad debt."
              stake="Debts past 90 days collect at under 50%" />
            <PainCard dot="var(--m)" cat="Pricing" title={'"Are we actually making money on this?"'}
              body="It finds the jobs and customers quietly losing margin and tells you exactly where to lift."
              stake="A 5% margin gap is real money every month" />
            <PainCard dot="var(--t)" cat="Big spend" title={'"Can we afford the van / the gear?"'}
              body="It runs the purchase against your real position and hands you three costed options."
              stake="One bad capex call can force a fire-sale" />
            <PainCard dot="var(--p)" cat="The big one" title={'"Is the business actually okay?"'}
              body="The question you carry into every weekend. It gives you a straight verdict — and tells you when it can't."
              stake="The dread you can finally put down" />
          </Reveal>
        </div>
      </section>

      {/* CONTRAST */}
      <section className="band-white pad" id="why">
        <div className="container">
          <Reveal className="center">
            <div className="eyebrow">Why it's different</div>
            <h2 style={{ fontSize: "clamp(30px,4.8vw,48px)", color: "var(--navy)", marginTop: 14 }}>
              Your software tells you what <em style={{ fontStyle: "normal", color: "var(--muted)" }}>happened.</em><br />
              Clearview tells you what to <span className="gtext">do.</span>
            </h2>
            <p className="sub-h">
              Even the new AI built into your accounting software can only answer what you ask — and it's not allowed to give advice. Clearview takes the call.
            </p>
          </Reveal>
          <Reveal className="pillars">
            <div className="pill-card"><div className="ic">↻</div><h3>Independent</h3><p>It works for you, not the software company — and it'll tell you when its own numbers are too stale to trust.</p></div>
            <div className="pill-card"><div className="ic">⚡</div><h3>On demand</h3><p>Ask it any time — daily, hourly, before a decision. No waiting for the quarter or the monthly close.</p></div>
            <div className="pill-card"><div className="ic">→</div><h3>Takes a side</h3><p>Not "here's a chart." A clear position on what to do — and the next move, named.</p></div>
          </Reveal>
        </div>
      </section>

      {/* PROOF */}
      <section className="band-grey pad" id="proof">
        <div className="container">
          <Reveal className="center">
            <div className="eyebrow">Watch it make a real call</div>
            <h2 style={{ fontSize: "clamp(30px,4.6vw,46px)", color: "var(--navy)", marginTop: 14 }}>
              A real question. A real answer.
            </h2>
          </Reveal>
          <Reveal className="proof-card">
            <div className="head">
              <span>Ask Clearview</span>
              <span>Synced from Xero · confidence high</span>
            </div>
            <div className="q"><span>Can we afford a new van — it's $90k?</span></div>
            <div className="pb">
              <div className="lab">Answer</div>
              <div className="ans">
                <span className="no">Not right now.</span> You've got $174k cash, but after setting aside ~$19k for BAS/PAYG/super (due 28 July) you're left with $155k. A $90k van drops you to $65k — barely one month of cover when your spending is ~$60k/mo.
              </div>
              <div className="note">Operational estimate, not tax advice — confirm with your accountant or BAS agent.</div>
              <div className="best">
                <div className="l">⚡ Best move now</div>
                <p>Chase the $14,218 overdue (MBC Fabrication $6,144, Brendale LDV $4,048, Novar $3,322) this week — that's 16% of the van price sitting unpaid.</p>
              </div>
              <div className="opts">
                <div className="opt"><div className="t">Safest</div><div className="h">Defer 90 days</div><p>Collect the overdue, fix April's margin, revisit when cash cover is back above 3 months.</p></div>
                <div className="opt"><div className="t">Growth</div><div className="h">Finance it</div><p>50% deposit, ~$1,200/mo lease. Keeps $110k liquid; rebuild the buffer in 30 days.</p></div>
                <div className="opt"><div className="t">Bold</div><div className="h">Buy outright</div><p>$90k cash, collect all $14k in 14 days, trim $928/mo to offset. High risk if June stays soft.</p></div>
              </div>
            </div>
          </Reveal>
          <Reveal className="center" style={{ marginTop: 34 }}>
            <Link className="pill" to="/apply" data-testid="proof-cta">Book a free demo</Link>
          </Reveal>
        </div>
      </section>

      {/* REPORTS */}
      <section className="band-white pad" id="reports">
        <div className="container">
          <Reveal className="center">
            <div className="eyebrow">The reports a CFO charges thousands for</div>
            <h2 style={{ fontSize: "clamp(30px,4.6vw,46px)", color: "var(--navy)", marginTop: 14 }}>
              Ask in plain English.<br /><span className="gtext">Get the document back.</span>
            </h2>
            <p className="sub-h">"Is the business looking good to a buyer?" → a full Sale Readiness report, written from your live numbers, in minutes.</p>
          </Reveal>
          <Reveal className="grid g3" style={{ marginTop: 44 }}>
            <div className="card rep"><div className="cat"><span className="d" />Borrowing</div><h3>Bank Loan Pack</h3><p>Lender-ready, straight from your numbers.</p></div>
            <div className="card rep"><div className="cat"><span className="d" />Exit</div><h3>Sale Readiness</h3><p>Is the business buyer-ready? Honestly.</p></div>
            <div className="card rep"><div className="cat"><span className="d" />Board</div><h3>Board Report</h3><p>The month in one clean page.</p></div>
            <div className="card rep"><div className="cat"><span className="d" />Cash</div><h3>Cash Crisis Report</h3><p>What to do tonight if it's tight.</p></div>
            <div className="card rep"><div className="cat"><span className="d" />Debtors</div><h3>Debtor Collection</h3><p>Who to chase, in order, with scripts.</p></div>
            <div className="card rep"><div className="cat"><span className="d" />Margin</div><h3>Profit Leak Report</h3><p>Where the money's slipping, ranked.</p></div>
          </Reveal>
        </div>
      </section>

      {/* REFUSAL */}
      <section className="band-dark pad center">
        <div className="container">
          <Reveal>
            <span className="refused-chip"><span className="d" />Refused 35 in 24h · revoked 24 · stale 11</span>
            <h2 style={{ fontSize: "clamp(30px,4.8vw,48px)", color: "#fff", marginTop: 24, maxWidth: 780, marginLeft: "auto", marginRight: "auto" }}>
              The only one that tells you<br />when <span className="gtext">not to trust it.</span>
            </h2>
            <p className="big-q">
              "We can't call this one yet — your live cash signals haven't reconciled this week. We'd rather say nothing than guess."
            </p>
            <p className="sub-h">
              Every other tool always gives you a number. Clearview tells you when the number can't be trusted — the one thing your software's built-in AI will never do, because it works for the software company.
            </p>
          </Reveal>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="band-grey pad">
        <div className="container">
          <Reveal className="center">
            <div className="eyebrow">Side by side</div>
            <h2 style={{ fontSize: "clamp(28px,4.4vw,42px)", color: "var(--navy)", marginTop: 14 }}>
              It can answer questions.<br />It can't make the call.
            </h2>
          </Reveal>
          <Reveal style={{ maxWidth: 840, margin: "40px auto 0" }}>
            <table className="cmp">
              <thead>
                <tr><th></th><th>Your software &amp; its AI</th><th>Clearview</th></tr>
              </thead>
              <tbody>
                <tr><td>Works for</td><td>The software company</td><td className="you">You</td></tr>
                <tr><td>When</td><td>When you ask</td><td className="you">Any time — daily, hourly</td></tr>
                <tr><td>Recommendations</td><td className="dim">"Consult a professional"</td><td className="you">Takes the position</td></tr>
                <tr><td>Across your systems</td><td>Itself only</td><td className="you">Xero · QuickBooks · WorkflowMax</td></tr>
                <tr><td>Tells you when it's unsure</td><td className="dim">Never</td><td className="you">Refuses, openly</td></tr>
              </tbody>
            </table>
          </Reveal>
        </div>
      </section>

      {/* TRUST */}
      <section className="band-white pad">
        <div className="container">
          <Reveal className="center">
            <div className="eyebrow">Why owners trust it</div>
            <h2 style={{ fontSize: "clamp(28px,4.4vw,42px)", color: "var(--navy)", marginTop: 14 }}>
              Built to be straight with you.
            </h2>
          </Reveal>
          <Reveal className="grid g3" style={{ marginTop: 44 }}>
            <div className="card"><div className="cat"><span className="d" style={{ background: "var(--t)" }} />Read-only</div><h3>It can't touch your money.</h3><p>Connect read-only in two minutes. Clearview reads — it never moves a cent or changes your books.</p></div>
            <div className="card"><div className="cat"><span className="d" style={{ background: "var(--p)" }} />Independent</div><h3>It's not your software vendor.</h3><p>No agenda to keep you on a platform. If your data's off, it says so.</p></div>
            <div className="card"><div className="cat"><span className="d" style={{ background: "var(--m)" }} />Plain English</div><h3>No jargon, no hype.</h3><p>It talks like an experienced operator, not an accountant or a chatbot. No "AI" theatre.</p></div>
          </Reveal>
        </div>
      </section>

      {/* OFFER */}
      <section className="offer pad" id="offer">
        <div className="container">
          <Reveal>
            <div className="inner">
              <h2>See it read your<br /><span className="gtext">business.</span></h2>
              <p>We sit down with you, connect Clearview read-only to your real numbers, and you watch it answer a decision you're actually weighing up. No slides, no pitch — just a straight read on your business.</p>
              <Link className="pill" to="/apply" data-testid="home-offer-cta">Book a free demo</Link>
              <p className="fine">A free, no-pitch demo · read-only · no lock-in</p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
    <Footer />
  </>
);

export default Home;
